import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { FaArrowLeft, FaUser, FaCalendar, FaTag, FaBuilding, FaFile, FaDownload, FaUsers } from "react-icons/fa";
import api from "../utils/api";
import CiteThis from "../components/CiteThis";

export default function ResearchView() {
  const { id } = useParams();
  const [research, setResearch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadResearch = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/research/${id}`);
      setResearch(response.data);
    } catch (err) {
      console.error("Error loading research:", err);
      setError("Failed to load research details. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadResearch();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-xl text-gray-600">Loading research details...</div>
        </div>
      </div>
    );
  }

  if (error || !research) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Research Not Found</h2>
            <p className="text-gray-600 mb-6">{error || "The research paper you're looking for doesn't exist."}</p>
            <Link
              to="/research"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <FaArrowLeft className="mr-2" />
              Back to Research List
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center py-6">
            <Link 
              to="/research" 
              className="flex items-center text-gray-600 hover:text-blue-600 transition-colors mr-8"
            >
              <FaArrowLeft className="mr-2" />
              Back to Research List
            </Link>
            <h1 className="text-2xl font-bold text-gray-900">Research Details</h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {/* Header Section */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-8">
            <h1 className="text-3xl font-bold mb-4">{research.title}</h1>
            <div className="flex items-center text-blue-100 mb-2">
              <FaUser className="mr-2" />
              <span className="font-medium">Authors:</span>
              <span className="ml-2">{research.authors?.join(", ") || "Unknown Authors"}</span>
            </div>
            <div className="flex items-center text-blue-100">
              <FaCalendar className="mr-2" />
              <span className="font-medium">Year:</span>
              <span className="ml-2">{research.year || "N/A"}</span>
            </div>
          </div>

          {/* Details Section */}
          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              {/* Left Column */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <FaBuilding className="mr-2 text-blue-600" />
                    Department & Campus
                  </h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-gray-700">
                      <span className="font-medium">Department:</span> {research.department || "N/A"}
                    </p>
                    <p className="text-gray-700 mt-1">
                      <span className="font-medium">Campus:</span> {research.campus || "N/A"}
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <FaTag className="mr-2 text-blue-600" />
                    Keywords
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {research.keywords && research.keywords.length > 0 ? (
                      research.keywords.map((keyword, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                        >
                          {keyword}
                        </span>
                      ))
                    ) : (
                      <p className="text-gray-500">No keywords available</p>
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <FaUsers className="mr-2 text-blue-600" />
                    Upload Information
                  </h3>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                    <p className="text-gray-700">
                      <span className="font-medium">Uploaded by:</span> {research.uploadedBy?.name || "Unknown"}
                    </p>
                    <p className="text-gray-700">
                      <span className="font-medium">Upload date:</span> {new Date(research.createdAt).toLocaleDateString()}
                    </p>
                    <p className="text-gray-700">
                      <span className="font-medium">Status:</span> 
                      <span className={`ml-2 px-2 py-1 rounded text-xs ${
                        research.status === 'approved' ? 'bg-green-100 text-green-800' :
                        research.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {research.status || 'Unknown'}
                      </span>
                    </p>
                    <p className="text-gray-700">
                      <span className="font-medium">Access:</span> 
                      <span className={`ml-2 px-2 py-1 rounded text-xs ${
                        research.isPublic ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {research.isPublic ? 'Public' : 'Restricted'}
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <FaFile className="mr-2 text-blue-600" />
                    Document Information
                  </h3>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                    <p className="text-gray-700">
                      <span className="font-medium">File name:</span> {research.pdfFilename || "N/A"}
                    </p>
                    <p className="text-gray-700">
                      <span className="font-medium">Views:</span> {research.viewCount || 0}
                    </p>
                    {research.reviewedBy && (
                      <p className="text-gray-700">
                        <span className="font-medium">Reviewed by:</span> {research.reviewedBy.name || "Unknown"}
                      </p>
                    )}
                    {research.reviewedAt && (
                      <p className="text-gray-700">
                        <span className="font-medium">Review date:</span> {new Date(research.reviewedAt).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>

                {research.adminComments && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Admin Comments</h3>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-700">{research.adminComments}</p>
                    </div>
                  </div>
                )}

                {/* Download Section */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Document Access</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    {research.isPublic ? (
                      <div className="space-y-3">
                        <p className="text-green-700 font-medium">This document is publicly available</p>
                        <button className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                          <FaDownload className="mr-2" />
                          Download PDF
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <p className="text-red-700 font-medium">This document requires special access</p>
                        <p className="text-gray-600 text-sm">
                          Please contact the research coordinator or administrator to request access.
                        </p>
                        <Link
                          to="/login"
                          className="inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                        >
                          Login to Request Access
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Abstract Section */}
            <div className="border-t pt-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Abstract</h3>
              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="text-gray-700 leading-relaxed">
                  {research.abstract || "No abstract available for this research paper."}
                </p>
              </div>
            </div>

            <div className="border-t pt-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Cite this research</h3>
              <CiteThis research={research} theme="light" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 